import { useAppRouter } from '@/common/route/useAppRouter';
import { timeUnits } from '@/common/utils/helpers';
import { validateDateAfter, validateFormat } from '@/common/validator/validatorHelpers';
import { useCrudActions } from '@/common/view/useCrudActions';
import { ROUTE_NAME_DUPLICATE } from '@/modules/duplicate/router';
import { useGameMatchList } from '@/modules/game/match/model/list';
import { duplicateService } from '@/services/duplicate';
import { useVuelidate } from '@vuelidate/core';
import { integer, minValue, required } from '@vuelidate/validators';
import { reactive, watch } from 'vue';

export function useDuplicateItemAdd() {
    const router = useAppRouter();

    const { fetchAllMatches, gameMatchOptions, gameMatchCache } = useGameMatchList();

    const model = reactive({
        name: null,
        type: 0,
        matchId: null,
        cost: 0,
        prizes: '',
        round: 0,
        game: 1,
        timeUnit: 0,
        timeDuration: 0,
        prepareSeconds: 0,
        openSeconds: 0,
        closeSeconds: 0,
        rankingSeconds: 0,
        publishTime: new Date(),
        unpublishTime: null,
        thumbnail: null,
        thumbnailFile: null
    });

    const validations = {
        name: { required },
        type: { required },
        matchId: { required },
        cost: { required, integer, min: minValue(0) },
        openSeconds: { required, integer, min: minValue(1) },
        closeSeconds: { required, integer, min: minValue(1) },
        rankingSeconds: { required, integer, min: minValue(0) },
        round: { required, integer, min: minValue(1) },
        game: { required, integer, min: minValue(1) },
        prizes: { required, format: validateFormat(/^\d+:\d+(;\d+:\d+)*$/) },
        publishTime: { required, validateDate: validateDateAfter() }
    };

    const v$ = useVuelidate(validations, model);

    const create = useCrudActions().excuteAndNotify({
        validator: v$,
        request: () =>
            duplicateService.item.create(
                model.name,
                model.type,
                model.matchId,
                model.cost,
                model.prizes,
                model.round,
                model.game,
                model.timeUnit,
                model.timeDuration,
                model.prepareSeconds,
                model.openSeconds,
                model.closeSeconds,
                model.rankingSeconds,
                model.publishTime.getTime(),
                model.unpublishTime?.getTime() ?? null,
                model.thumbnailFile
            ),
        onSuccess: () => router.push({ name: ROUTE_NAME_DUPLICATE.ITEM.INDEX })
    });

    watch(
        () => model.matchId,
        matchId => {
            const match = gameMatchCache.value.get(matchId);
            model.cost = match.data.cost;
            model.round = match.data.maxRound;
        }
    );

    return { model, v$, fetchAllMatches, gameMatchOptions, create, timeUnits };
}
